import {useOutletContext} from "react-router";
import type {PtV2Settings, PtV2Summary, PtV2TabKey} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";

/** Shared by the Payment Terms v2 layout with its tab routes. */
export type PtV2OutletContext = {
    summary: PtV2Summary | undefined;
    summaryLoading: boolean;
    summaryError: boolean;
    /** True once workpapers have been synced or imported for this databag. */
    hasData: boolean;
    /** Mithra staff group: sees the Data management section (sync and import). */
    isStaff: boolean;
    tabPath: (tab: PtV2TabKey) => string;
    /** The tabs this user sees, in order; `hidden` marks the ones staff hid from customers (staff only). */
    tabs: PtV2VisibleTab[];
    /** Cost of capital (% a year) from the settings; null when not set. Annual value = working capital × rate. */
    costOfCapitalPct: number | null;
};

export type PtV2VisibleTab = {key: PtV2TabKey; label: string; hidden: boolean};

export const usePtV2Context = () => useOutletContext<PtV2OutletContext>();

export const PT_V2_TABS: {key: PtV2TabKey; label: string}[] = [
    {key: 'overview', label: 'Overview'},
    {key: 'leakage', label: 'Payment term leakage'},
    {key: 'harmonization', label: 'Term harmonization'},
    {key: 'discounts', label: 'Discount capture'},
    {key: 'actions', label: 'Action plan'},
    {key: 'sources', label: 'Data sources'},
];

/** Tabs for staff only, whatever the settings say. */
export const PT_V2_STAFF_TABS: PtV2TabKey[] = ['sources'];

/**
 * The tabs this user sees: customers lose the staff-only tabs and the ones staff hid; staff see every tab, the
 * hidden ones marked. Customers get Overview only until the settings load (and if they cannot be loaded), so a tab
 * staff hid never shows for a moment.
 */
export function visiblePtV2Tabs(isStaff: boolean, settings: PtV2Settings | undefined): PtV2VisibleTab[] {
    const hidden = new Set(settings?.hidden_tabs ?? []);
    return PT_V2_TABS
        .filter(t => isStaff || (settings
            ? !PT_V2_STAFF_TABS.includes(t.key) && !hidden.has(t.key)
            : t.key === 'overview'))
        .map(t => ({...t, hidden: hidden.has(t.key)}));
}
